import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Pencil, Trash2, MessagesSquare, FileBarChart, BellRing, ArrowUpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { MetricDialog } from "@/components/semantics/MetricDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useSession } from "@/context/AuthContext";
import { DOMAIN_BADGE, METRIC_STATUS, canReviewRole } from "@/lib/semantics";
import { SEMANTICS } from "@/constants/testIds";
import api from "@/lib/api";

export default function MetricDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { active } = useWorkspace();
  const { user } = useSession();
  const canReview = canReviewRole(user?.role);
  const [metric, setMetric] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/semantics/metrics/${id}`);
      setMetric(data);
    } catch {
      toast.error("Metric not found");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const promote = async () => {
    await api.patch(`/semantics/metrics/${id}`, { status: "active" });
    toast.success("Promoted to active");
    await load();
  };

  const remove = async () => {
    try {
      await api.delete(`/semantics/metrics/${id}`);
      toast.success("Metric deleted");
      navigate("/semantics/metrics");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!metric) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <WorkspaceNav />
        <div className="flex items-center justify-center py-32 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
      </div>
    );
  }

  const st = METRIC_STATUS[metric.status] || METRIC_STATUS.draft;
  const usage = metric.usage || { chat_answers: 0, reports: 0, alerts: 0 };

  return (
    <div data-testid={SEMANTICS.metricDetailRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-4xl px-6 py-8">
        <Link to="/semantics/metrics" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Metrics
        </Link>

        <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{metric.name}</h1>
              <Badge variant="outline" className={`rounded-full px-2 py-0 text-[10px] ${DOMAIN_BADGE[metric.domain] || ""}`}>{metric.domain}</Badge>
              <Badge variant="outline" className={`rounded-full px-2 py-0 text-[10px] ${st.cls}`}>{st.label}</Badge>
            </div>
            {metric.definition && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{metric.definition}</p>}
            {metric.synonyms?.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">Also known as: {metric.synonyms.join(", ")}</p>
            )}
          </div>
          {canReview && (
            <div className="flex items-center gap-2">
              {metric.status === "draft" && (
                <Button size="sm" variant="outline" onClick={promote} className="rounded-full border-emerald-500/40 text-xs uppercase tracking-[0.12em] text-emerald-400 hover:bg-emerald-500/10">
                  <ArrowUpCircle className="mr-1.5 h-3.5 w-3.5" /> Promote
                </Button>
              )}
              <Button size="sm" variant="outline" data-testid={SEMANTICS.metricEdit} onClick={() => setEditOpen(true)} className="rounded-full border-border/70 text-xs">
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid={SEMANTICS.metricDelete} className="rounded-full border-red-500/40 text-xs text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="font-mono">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this metric?</AlertDialogTitle>
                    <AlertDialogDescription>This removes the metric definition. Chat answers already given are unaffected.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={remove} data-testid={SEMANTICS.metricDeleteConfirm} className="bg-red-600 text-xs hover:bg-red-600/90">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>

        {/* Usage */}
        <div className="mt-8 grid grid-cols-3 gap-3">
          <UsageCard testid={SEMANTICS.usageChat} icon={MessagesSquare} value={usage.chat_answers} label="chat answers" />
          <UsageCard testid={SEMANTICS.usageReports} icon={FileBarChart} value={usage.reports} label="saved reports" />
          <UsageCard testid={SEMANTICS.usageAlerts} icon={BellRing} value={usage.alerts} label="alerts" />
        </div>

        {/* Definition blocks */}
        <div className="mt-8 space-y-6">
          {metric.semantic_expression && (
            <Block label="Semantic expression"><p className="text-sm">{metric.semantic_expression}</p></Block>
          )}
          <Block label="SQL template">
            {metric.sql_template
              ? <pre className="max-h-64 overflow-auto rounded-lg border border-border/60 bg-background/60 p-3 text-[11px] leading-relaxed whitespace-pre-wrap">{metric.sql_template}</pre>
              : <p className="text-xs text-muted-foreground">No SQL template set — the planner derives SQL from the definition.</p>}
          </Block>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <ChipBlock label="Default grain" items={metric.default_grain} />
            <ChipBlock label="Dimensions" items={metric.dimensions} />
            <ChipBlock label="Valid filters" items={metric.valid_filters} />
            <ChipBlock label="Source tables" items={metric.source_tables} />
          </div>
          {metric.owner_name && (
            <p className="text-xs text-muted-foreground">Owner: {metric.owner_name} · origin: {metric.origin}</p>
          )}
        </div>
      </main>

      <MetricDialog open={editOpen} onOpenChange={setEditOpen} workspaceId={active?.id} metric={metric} onSaved={load} />
    </div>
  );
}

function UsageCard({ testid, icon: Icon, value, label }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border/60 bg-card/40 p-4">
      <Icon className="h-4 w-4 text-accent" />
      <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
    </div>
  );
}

function Block({ label, children }) {
  return (
    <div>
      <div className="mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function ChipBlock({ label, items }) {
  return (
    <Block label={label}>
      {items?.length ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it) => <span key={it} className="rounded-full border border-border/60 bg-card/50 px-2 py-0.5 text-[11px] text-muted-foreground">{it}</span>)}
        </div>
      ) : <p className="text-xs text-muted-foreground">—</p>}
    </Block>
  );
}
